// useOSDWatch
// -----------
// Custom React hook — realtime Supabase subscription for OSD code changes.
// When the polling utility writes an OSD code to children_uniforms for any of
// THIS request's children, refresh the request so the UI shows the new code
// immediately. Side-effect only — returns nothing.
// TypeScript source — useOSDWatch.js is a thin re-export stub.

import type { RealtimeChannel } from "@supabase/supabase-js";
import React from "react";
import { getRequestChildren, listChildIdsByRequestId } from "../data/requests";
import { getSupabaseClient } from "../utils/supabase";

export function useOSDWatch(
  requestId: number | string | null | undefined,
  req: Record<string, unknown>,
  onSaveNoDb?: ((updatedReq: Record<string, unknown>) => void) | null
) {
  // Refs so the callback always uses the LATEST onSaveNoDb / req, not stale
  // closure values from when the effect was first set up.
  var reqRef = React.useRef(req);
  reqRef.current = req;
  var onSaveNoDbRef = React.useRef(onSaveNoDb);
  onSaveNoDbRef.current = onSaveNoDb;

  React.useEffect(
    function () {
      var supabaseClient = getSupabaseClient();
      if (!supabaseClient || !requestId) return;
      // Capture the narrowed (non-null) client so it stays typed inside the
      // subscription closures below.
      var client = supabaseClient;
      var cancelled = false;
      var channel: RealtimeChannel | null = null;

      // Get the child IDs for this request so we only react to relevant updates.
      listChildIdsByRequestId(requestId).then(function (childIds) {
        if (cancelled) return;
        if (childIds.length === 0) return;

        channel = client
          .channel(`osd-watch-${requestId}`)
          .on(
            "postgres_changes",
            {
              event: "UPDATE",
              schema: "public",
              table: "children_uniforms",
            },
            function (payload: { new?: Record<string, unknown>; old?: Record<string, unknown> }) {
              // Only react when an OSD code was just set (was null, now has value)
              // AND the row belongs to this request.
              var newRow = payload.new || {};
              var oldRow = payload.old || {};
              if (!newRow.osd_code) return;
              if (oldRow.osd_code === newRow.osd_code) return;
              if (childIds.indexOf(newRow.children_id as string) < 0) return;

              // Refetch this request's children via the RPC and merge into req.
              getRequestChildren(requestId).then(function (r) {
                if (r.error || cancelled) return;
                var cb = onSaveNoDbRef.current;
                if (cb) {
                  cb(Object.assign({}, reqRef.current, { children: r.data }));
                }
              });
            }
          )
          .subscribe();
      });

      return function () {
        cancelled = true;
        if (channel) client.removeChannel(channel);
      };
    },
    [requestId]
  );
}
